import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Box, Typography } from '@mui/material'
import InCardStack from 'components/atoms/container/InCardStack'
import { getLocalUser } from 'utils/localStorage'
import {
  getGoalInfoById,
  getCohortGoalFacilitation
} from 'utils/getSupportingInfo'
import { useTranslation } from 'react-i18next'
import JourneyStepCard from './JourneyStepCard'
import colors from 'styles/colors'
import moment from 'moment'
import { Tag } from 'components/atoms/Text'
import { formatDateShort } from 'utils/formatDateTime'
import RowStack from 'components/atoms/container/RowStack'

const JourneyPath = ({
  organizedSteps,
  clickHandler,
  currentStepNumber,
  journeyCompleted = false,
  activeStepNumber,
  activeL1StepNumber,
  goalId
}) => {
  const { t } = useTranslation()
  const user = getLocalUser()
  const [goalSteps, setGoalSteps] = useState([])
  const [goalSessions, setGoalSessions] = useState([])

  useEffect(() => {
    const fetchGoalSessions = async () => {
      const goalInfo = await getGoalInfoById(parseInt(goalId))
      const { cohortSession } = goalInfo
      let sessionData
      if (cohortSession) {
        sessionData = await getCohortGoalFacilitation(
          cohortSession.id,
          organizedSteps
        )
      } else {
        sessionData = organizedSteps
      }

      const sessionSteps = {}
      const withoutSessionSteps = []

      sessionData.forEach((step, i) => {
        step.index = i // Original index requied
        const session = step.cohortSession
        if (session) {
          if (!sessionSteps[session.facilitationDate]) {
            sessionSteps[session.facilitationDate] = {
              facilitationDate: session.facilitationDate,
              steps: [step]
            }
          } else {
            sessionSteps[session.facilitationDate].steps.push(step)
          }
        } else {
          withoutSessionSteps.push(step)
        }
      })
      const gs = [
        ...Object.values(sessionSteps),
        ...[{ steps: withoutSessionSteps }]
      ]
      setGoalSessions(gs)
    }
    if (goalId && organizedSteps.length > 0) {
      setGoalSteps(organizedSteps)
      if (user?.isCohortUser) fetchGoalSessions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId, organizedSteps])

  useEffect(() => {
    if (organizedSteps.length > 0) {
      if (activeStepNumber) {
        setTimeout(() => {
          document
            .getElementById(`step-${activeStepNumber}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 400)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizedSteps, activeStepNumber])

  let todoSessionIndex = -1
  const SessionWrapper = ({ children, session, index }) => {
    const allStepsCompleted = session.steps[
      session.steps.length - 1
    ]?.childL2Steps?.every(step => step.number < currentStepNumber)

    const isOverDue =
      session.facilitationDate && !allStepsCompleted
        ? moment()
            .startOf('day')
            .isAfter(moment(session.facilitationDate).startOf('day'))
        : false

    const isTodo =
      session.steps.some(
        step =>
          step.number > currentStepNumber ||
          step.childL2Steps?.some(
            childStep => childStep.number === currentStepNumber
          )
      ) &&
      !isOverDue &&
      todoSessionIndex === -1
    if (isTodo && todoSessionIndex === -1) todoSessionIndex = index

    // Next is Upcoming if previous session is in Todo state
    const isUpcoming = todoSessionIndex > -1 ? index > todoSessionIndex : false

    return (
      <>
        {session.facilitationDate ? (
          <Box
            style={{ marginTop: index === 0 ? 16 : 32 }}
            sx={{
              borderRadius: 4,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: isTodo ? colors.Green200 : colors.Grey100,
              backgroundColor: isTodo ? colors.Green100 : colors.MetalGrey50,
              padding: '20px 12px 12px 12px',
              position: 'relative'
            }}>
            <Box
              sx={{
                paddingX: 4,
                paddingY: 2,
                borderRadius: 2,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: isTodo
                  ? colors.Green200
                  : isOverDue
                    ? colors.Peach400
                    : colors.Grey100,
                backgroundColor: isOverDue ? colors.Peach50 : colors.White,
                width: 'max-content',
                position: 'absolute',
                top: '-15px',
                left: '50%',
                transform: 'translateX(-50%)'
              }}>
              <RowStack spacing={3} width={'auto'}>
                <Tag
                  variant={'eyebrow'}
                  sx={{
                    padding: 2,
                    color: isOverDue
                      ? colors.Watermelon400
                      : colors.MidnightTeal400,
                    backgroundColor: isTodo
                      ? colors.Green200
                      : isOverDue
                        ? colors.Peach100
                        : colors.Grey100,
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: '110%'
                  }}>
                  {isTodo
                    ? t('pathsAndModels.toDo')
                    : isOverDue
                      ? `${t('pathsAndModels.overdue')}!`
                      : isUpcoming
                        ? t('pathsAndModels.upcoming')
                        : t('common.done')}
                </Tag>
                <Typography
                  variant='body1Micro'
                  color={
                    isOverDue ? colors.Watermelon400 : colors.MidnightTeal400
                  }
                  fontWeight={500}>
                  {isTodo || isUpcoming
                    ? `${t('pathsAndModels.dueBySession')} `
                    : isOverDue && `${t('pathsAndModels.sessionClosed')} `}
                  {formatDateShort(session.facilitationDate)}
                </Typography>
              </RowStack>
            </Box>
            <InCardStack spacing={6}>{children}</InCardStack>
          </Box>
        ) : (
          <>{children}</>
        )}
      </>
    )
  }

  const GoalStep = ({ l1, i }) => (
    <JourneyStepCard
      l1={l1}
      index={i}
      goalSteps={goalSteps}
      currentStepNumber={currentStepNumber}
      journeyCompleted={journeyCompleted}
      activeStepNumber={activeStepNumber}
      activeL1StepNumber={activeL1StepNumber}
      clickHandler={clickHandler}
    />
  )

  return (
    <Box
      sx={{
        height: 'calc(100vh - 210px)',
        overflow: 'auto',
        paddingRight: 4
      }}>
      <InCardStack spacing={6}>
        {user?.isCohortUser ? (
          <>
            {goalSessions.map((session, i) => {
              return (
                <SessionWrapper key={uuidv4()} session={session} index={i}>
                  {session?.steps?.map(l1 => (
                    <GoalStep key={uuidv4()} l1={l1} i={l1.index} />
                  ))}
                </SessionWrapper>
              )
            })}
          </>
        ) : (
          <>
            {goalSteps.map((l1, i) => (
              <GoalStep key={uuidv4()} l1={l1} i={i} />
            ))}
          </>
        )}
      </InCardStack>
    </Box>
  )
}

export default JourneyPath
